import * as React from "react"
import { Link, useRouter } from "@tanstack/react-router"
import { useServerFn } from "@tanstack/react-start"
import {
  Building2,
  ChevronDown,
  LogOut,
  Map,
  Menu,
  Search,
  UserRound,
} from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { APP_NAME } from "@/lib/constants"
import type { PublicDemoResetStatus } from "@/lib/demo-reset-contract"
import { logoutFn, subscribeNewsletterFn } from "@/lib/app.functions"
import { useLocale } from "@/lib/i18n"
import type { Locale } from "@/lib/i18n"
import type { User } from "@/lib/types"

const navItems = [
  { to: "/search", key: "buy", search: { transaction: "buy" } },
  { to: "/search", key: "rent", search: { transaction: "rent" } },
  { to: "/my-listings", key: "sell" },
  { to: "/search", key: "properties" },
  { to: "/events", key: "openHouses" },
  { to: "/news", key: "notes" },
] as const

export function AppShell({
  user,
  children,
}: {
  user: User | null
  children: React.ReactNode
}) {
  return (
    <div className="flex min-h-svh flex-col">
      <DemoBanner />
      <SiteHeader user={user} />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  )
}

function DemoBanner() {
  const [status, setStatus] = React.useState<PublicDemoResetStatus | null>(null)
  const [statusUnavailable, setStatusUnavailable] = React.useState(false)

  React.useEffect(() => {
    let disposed = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const controller = new AbortController()

    const loadStatus = async () => {
      try {
        const response = await fetch("/api/demo-reset-status", {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
          signal: controller.signal,
        })
        const next: PublicDemoResetStatus = await response.json()
        if (!disposed) {
          setStatus(next)
          setStatusUnavailable(next.state === "unavailable")
          const active = next.state === "running" || next.state === "delayed"
          timer = setTimeout(loadStatus, active ? 5_000 : 60_000)
        }
      } catch {
        if (!disposed && !controller.signal.aborted) {
          setStatusUnavailable(true)
          timer = setTimeout(loadStatus, 60_000)
        }
      }
    }

    void loadStatus()
    return () => {
      disposed = true
      controller.abort()
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [])

  const formatResetTime = (value: string, timeZone: string) =>
    new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone,
      timeZoneName: "short",
    }).format(new Date(value))

  const stateLabel =
    status?.state === "disabled"
      ? "Automatic reset execution is disabled"
      : status?.state === "running"
        ? "Reset in progress; editing is temporarily paused"
        : status?.state === "delayed"
          ? "Reset is taking longer than expected; editing remains paused"
          : status?.state === "failed"
            ? "Last reset failed; editing remains paused"
            : status?.state === "overdue"
              ? "Reset is overdue"
              : status?.state === "unavailable"
                ? "Reset status unavailable"
                : "Reset scheduled"

  return (
    <div
      className="demo-banner border-b border-foreground bg-accent px-4 py-2 text-center font-heading text-xs font-bold tracking-[0.12em] text-accent-foreground uppercase"
      role="status"
      aria-live="polite"
      data-reset-state={status?.state ?? "unknown"}
      data-reset-generation={status?.resetGeneration ?? 0}
    >
      {status ? (
        <>
          Demo data reset · Demo environment —{" "}
          {status.state === "disabled" ? (
            <>The automatic daily reset is currently disabled.</>
          ) : (
            <>
              The daily reset starts at {status.scheduleLocalTime} Philippine
              Time ({status.scheduleTimeZone}, {status.scheduleUtcOffset}). Next
              reset:{" "}
              <time dateTime={status.nextScheduledResetAt}>
                {formatResetTime(
                  status.nextScheduledResetAt,
                  status.scheduleTimeZone
                )}
              </time>
              .
            </>
          )}{" "}
          Last completed:{" "}
          {status.lastSuccessfulResetAt ? (
            <time dateTime={status.lastSuccessfulResetAt}>
              {formatResetTime(
                status.lastSuccessfulResetAt,
                status.scheduleTimeZone
              )}
            </time>
          ) : (
            "Not yet completed"
          )}
          . {stateLabel}. Do not enter sensitive information.
        </>
      ) : statusUnavailable ? (
        <>
          Demo data reset · Reset status unavailable. Do not enter sensitive
          information.
        </>
      ) : (
        <>Portfolio demo · Shared synthetic records · Loading reset status</>
      )}{" "}
      · Admin edits affect every visitor
    </div>
  )
}

function BrandMark({ className }: { className?: string }) {
  return (
    <img
      src="/brand/harbor-pine-mark.svg"
      alt=""
      width={64}
      height={64}
      className={className}
      aria-hidden="true"
    />
  )
}

function Wordmark() {
  return (
    <Link
      to="/"
      className="group flex shrink-0 items-center gap-3"
      aria-label={`${APP_NAME} home`}
    >
      <BrandMark className="size-10 border border-foreground" />
      <span className="hidden sm:grid">
        <span className="font-heading text-xl leading-none font-bold tracking-[-0.02em] uppercase">
          Harbor <span className="text-primary">&</span> Pine
        </span>
        <span className="mt-1 font-heading text-[9px] leading-none font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          Realty · Search & advisory
        </span>
      </span>
    </Link>
  )
}

function MainNav({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useLocale()
  return (
    <nav aria-label="Primary navigation" className="contents">
      {navItems.map((item) => (
        <Link
          key={item.key}
          to={item.to}
          search={"search" in item ? item.search : undefined}
          onClick={onNavigate}
          className="border-b-2 border-transparent py-2 font-heading text-sm font-semibold tracking-[0.04em] uppercase hover:border-primary hover:text-primary [&.active]:border-primary [&.active]:text-primary"
        >
          {t(item.key)}
        </Link>
      ))}
    </nav>
  )
}

function LocalePicker() {
  const { locale, setLocale } = useLocale()
  return (
    <label className="flex items-center gap-2">
      <span className="sr-only">Language</span>
      <select
        aria-label="Language"
        value={locale}
        onChange={(event) => setLocale(event.target.value as Locale)}
        className="h-9 border border-border bg-white px-2 font-heading text-xs font-bold uppercase"
      >
        <option value="en">EN</option>
        <option value="fr">FR</option>
        <option value="zh">中文</option>
      </select>
    </label>
  )
}

function AccountMenu({ user }: { user: User | null }) {
  const router = useRouter()
  const logout = useServerFn(logoutFn)
  const { t } = useLocale()

  if (!user) {
    return (
      <Button
        className="field-button"
        variant="outline"
        nativeButton={false}
        render={<Link to="/login" search={{ next: undefined }} />}
      >
        <UserRound />
        {t("signIn")}
      </Button>
    )
  }

  const handleLogout = async () => {
    await logout()
    await router.invalidate()
    toast.success("You have been signed out.")
    await router.navigate({ to: "/" })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button variant="outline" className="field-button max-w-48">
            <UserRound />
            <span className="truncate">{user.name}</span>
            <ChevronDown />
          </Button>
        }
      />
      <DropdownMenuContent align="end" className="w-64 rounded-none">
        <DropdownMenuGroup>
          <DropdownMenuLabel>
            <span className="block truncate">{user.name}</span>
            <span className="block truncate text-xs font-normal text-muted-foreground">
              {user.email}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem render={<Link to="/dashboard" />}>
            <Map />
            {t("dashboard")}
          </DropdownMenuItem>
          {user.role === "admin" ? (
            <DropdownMenuItem render={<Link to="/admin" />}>
              <Building2 />
              Administration
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function SiteHeader({ user }: { user: User | null }) {
  const [open, setOpen] = React.useState(false)
  const { t } = useLocale()

  return (
    <header className="site-header sticky top-0 z-40 border-b border-border bg-background">
      <div className="field-container flex h-16 items-center justify-between gap-5">
        <Wordmark />
        <div className="hidden items-center gap-5 xl:flex">
          <MainNav />
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-none"
            nativeButton={false}
            render={<Link to="/search" />}
            aria-label={t("search")}
          >
            <Search />
          </Button>
          <LocalePicker />
          <div className="hidden sm:block">
            <AccountMenu user={user} />
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              render={
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-none xl:hidden"
                  aria-label={t("menu")}
                >
                  <Menu />
                </Button>
              }
            />
            <SheetContent className="rounded-none p-0">
              <SheetHeader className="border-b p-5">
                <SheetTitle className="text-left uppercase">
                  Plan a move
                </SheetTitle>
              </SheetHeader>
              <div className="flex flex-col gap-1 p-5">
                <MainNav onNavigate={() => setOpen(false)} />
                <div className="mt-5 border-t pt-5 sm:hidden">
                  <AccountMenu user={user} />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}

function SiteFooter() {
  const subscribe = useServerFn(subscribeNewsletterFn)
  const [pending, setPending] = React.useState(false)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setPending(true)
    const form = new FormData(event.currentTarget)
    try {
      await subscribe({ data: { email: String(form.get("email") ?? "") } })
      event.currentTarget.reset()
      toast.success("You are on the field-notes list.")
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Unable to subscribe."
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <footer className="site-footer mt-16 border-t-2 border-foreground bg-foreground text-white">
      <div className="field-container grid gap-10 py-12 lg:grid-cols-[1.3fr_1fr_1fr]">
        <section>
          <div className="flex items-center gap-4">
            <BrandMark className="size-12 shrink-0 border border-white/30" />
            <div>
              <p className="font-heading text-3xl leading-none font-bold uppercase">
                Harbor <span className="text-accent">&</span> Pine
              </p>
              <p className="mt-1 font-heading text-[10px] font-semibold tracking-[0.2em] text-white/60 uppercase">
                Realty · Search & advisory
              </p>
            </div>
          </div>
          <p className="mt-4 max-w-md text-sm leading-6 text-white/70">
            A fictional real-estate marketplace built to demonstrate discovery,
            member, owner, and administration workflows with synthetic data.
          </p>
        </section>
        <nav
          aria-label="Footer navigation"
          className="grid grid-cols-2 gap-3 text-sm"
        >
          <Link to="/about" className="hover:text-accent">
            Company
          </Link>
          <Link
            to="/contact"
            search={{ subject: undefined }}
            className="hover:text-accent"
          >
            Contact
          </Link>
          <Link
            to="/about/$slug"
            params={{ slug: "privacy" }}
            className="hover:text-accent"
          >
            Privacy
          </Link>
          <Link
            to="/about/$slug"
            params={{ slug: "terms" }}
            className="hover:text-accent"
          >
            Terms
          </Link>
          <Link to="/sitemap" className="hover:text-accent">
            Sitemap
          </Link>
          <Link
            to="/login"
            search={{ next: undefined }}
            className="hover:text-accent"
          >
            Demo access
          </Link>
        </nav>
        <form onSubmit={handleSubmit}>
          <label htmlFor="newsletter" className="field-label text-white/65">
            Market field notes
          </label>
          <p className="mt-2 text-xs leading-5 text-white/60">
            Demo only: use a fictional address. This stores a synthetic
            subscriber record; no email is sent.
          </p>
          <div className="mt-3 flex">
            <Input
              id="newsletter"
              name="email"
              type="email"
              required
              placeholder="you@example.com"
              className="field-input border-white/40 bg-white text-foreground"
            />
            <Button
              type="submit"
              disabled={pending}
              className="field-button h-11 bg-accent text-accent-foreground hover:bg-accent/85"
            >
              {pending ? "Adding…" : "Join"}
            </Button>
          </div>
        </form>
      </div>
      <div className="border-t border-white/20">
        <div className="field-container flex flex-wrap justify-between gap-3 py-4 text-xs text-white/55">
          <span>© 2026 Harbor & Pine Realty</span>
          <span>Fictional marketplace demo · No real transactions</span>
          <a
            href="https://jericop.dev"
            rel="author"
            className="font-semibold text-white underline decoration-white/35 underline-offset-4 hover:decoration-white"
          >
            Built by Jerico Pulvera
          </a>
        </div>
      </div>
    </footer>
  )
}
